import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WBAlert, WBBox, WBButton, WBTextField } from '@admiin-com/ds-web';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { PATHS } from '../../navigation/paths';
import { useResendTemporaryPassword } from '../../hooks/useResendTemporaryPassword/useResendTemporaryPassword';
import { useClientContext } from '../../components/ApolloClientProvider/ApolloClientProvider';
import { configureAppSyncClient } from '../../helpers/appsync';

interface EmailFormData {
  email: string;
}

interface ResetFormData {
  code: string;
  password: string;
  confirmPassword: string;
}

const resolver =
  <T extends z.ZodTypeAny>(schema: T) =>
  async (values: unknown) => {
    const result = schema.safeParse(values);
    if (result.success) {
      return { values: result.data, errors: {} };
    }
    const formErrors: Record<string, { type: string; message: string }> = {};
    const fieldErrors = result.error.flatten().fieldErrors;
    Object.keys(fieldErrors).forEach((key) => {
      const message = fieldErrors[key]?.[0];
      if (message) {
        formErrors[key] = { type: 'validation', message };
      }
    });
    return { values: {}, errors: formErrors };
  };

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [tempPasswordResent, setTempPasswordResent] = useState(false);

  const { output } = useClientContext();

  const [resendTemporaryPassword] = useResendTemporaryPassword();

  const emailSchema = z.object({
    email: z.string().email(t('emailRequired') as string),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailFormData>({ resolver: resolver(emailSchema) });

  const resetSchema = z
    .object({
      code: z.string().min(1, t('codeRequired') as string),
      password: z.string().min(1, t('newPasswordRequired') as string),
      confirmPassword: z
        .string()
        .min(1, t('confirmPasswordRequired') as string),
    })
    .refine((d) => d.password === d.confirmPassword, {
      message: t('confirmNotMatch') as string,
      path: ['confirmPassword'],
    });

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: resetErrors },
  } = useForm<ResetFormData>({ resolver: resolver(resetSchema) });

  const handleResendTemporaryPassword = async (email: string) => {
    setTempPasswordResent(false);
    setError('');
    setCodeSent(false);

    const emailTrimmed = email.toLowerCase().trim();
    setSubmittedEmail(emailTrimmed);

    if (!output?.graphQLAPIURL) {
      setError(t('errorResendingTemporaryPassword'));
      return;
    }

    try {
      const { data } = await resendTemporaryPassword({
        variables: {
          input: {
            email: emailTrimmed,
          },
        },
        client: configureAppSyncClient({
          authType: 'IAM',
          graphQLAPIURL: output.graphQLAPIURL,
        }),
      });

      if (data?.resendUserTemporaryPassword) {
        setTempPasswordResent(true);
      } else {
        setError(t('errorResendingTemporaryPassword'));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
    }
  };

  const onSendCode = async ({ email }: EmailFormData) => {
    setError('');
    setTempPasswordResent(false);
    try {
      await resetPassword({ username: email.toLowerCase().trim() });
      setSubmittedEmail(email.toLowerCase().trim());
      setCodeSent(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      if (message === 'User password cannot be reset in the current state.') {
        await handleResendTemporaryPassword(email);
      } else {
        setError(message);
      }
    }
  };

  const onReset = async ({ code, password }: ResetFormData) => {
    setError('');
    try {
      await confirmResetPassword({
        username: submittedEmail,
        confirmationCode: code,
        newPassword: password,
      });
      navigate(PATHS.signIn);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
    }
  };

  return (
    <WBBox maxWidth={400} mx="auto" mt={8}>
      {error && (
        <WBAlert severity="error" sx={{ mb: 2 }}>
          {error}
        </WBAlert>
      )}
      {tempPasswordResent && (
        <WBAlert severity="success" sx={{ mb: 2 }}>
          {t('temporaryPasswordResent')}
        </WBAlert>
      )}
      {!codeSent ? (
        <WBBox as="form" onSubmit={handleSubmit(onSendCode)}>
          <WBTextField
            fullWidth
            label={t('emailTitle')}
            margin="normal"
            {...register('email')}
            error={!!errors.email}
            helperText={errors.email?.message}
          />
          <WBButton type="submit" fullWidth sx={{ mt: 2 }}>
            {t('resetPasswordTitle')}
          </WBButton>
          <WBButton
            variant="text"
            fullWidth
            component={Link}
            to={PATHS.signIn}
            sx={{ mt: 1 }}
          >
            {t('backToLogin')}
          </WBButton>
        </WBBox>
      ) : (
        <WBBox as="form" onSubmit={handleSubmitReset(onReset)}>
          <WBTextField
            fullWidth
            label={t('codeTitle')}
            margin="normal"
            {...registerReset('code')}
            error={!!resetErrors.code}
            helperText={resetErrors.code?.message}
          />
          <WBTextField
            fullWidth
            type="password"
            label={t('newPasswordTitle')}
            margin="normal"
            {...registerReset('password')}
            error={!!resetErrors.password}
            helperText={resetErrors.password?.message}
          />
          <WBTextField
            fullWidth
            type="password"
            label={t('confirmPasswordTitle')}
            margin="normal"
            {...registerReset('confirmPassword')}
            error={!!resetErrors.confirmPassword}
            helperText={resetErrors.confirmPassword?.message}
          />
          <WBButton type="submit" fullWidth sx={{ mt: 2 }}>
            {t('submit')}
          </WBButton>
        </WBBox>
      )}
    </WBBox>
  );
};

export default ResetPassword;
